import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Tooltip } from "@mui/material";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { TAX_TYPE } from "utils/constants";
import {
  getModifierDisplayParts,
  getModifierOptionGroupLabel,
  getModifierRegion,
  getRemovedModifiersFromDescription,
  isPizzaOrderDetail,
} from "utils/orderDisplay";
import { OrderItem } from "utils/types";

type OrderCardType = OrderItem & {
  taxData?: any;
  onDoubleClick?: () => void;
  isNew?: boolean;
};

const OrderCard = (item: OrderCardType & { timeZone: string }) => {
  const [showProducts, setshowProducts] = useState(true);

  const formatDate = (value: any) => {
    const parsed = new Date(String(value || ""));
    return Number.isNaN(parsed.getTime()) ? String(value || "--") : format(parsed, "MM/dd/yyyy");
  };

  const formatTime = (value: any) => {
    const raw = String(value || "").trim();
    if (!raw) return "--";

    const parsed = new Date(`2000-01-01T${raw}`);
    return Number.isNaN(parsed.getTime()) ? raw : format(parsed, "HH:mm:ss");
  };

  const formatRequestedDateTime = (dateValue: any, timeValue: any) => {
    const dateText = String(dateValue || "").trim();
    const timeText = String(timeValue || "").trim();
    if (!dateText && !timeText) return "--";

    const parsed = new Date(
      timeText && /^\d{4}-\d{2}-\d{2}$/.test(dateText)
        ? `${dateText}T${timeText}`
        : [dateText, timeText].filter(Boolean).join(" "),
    );

    if (!Number.isNaN(parsed.getTime())) {
      return format(parsed, "MM/dd/yyyy h:mm aa");
    }

    return [dateText, timeText].filter(Boolean).join(" ");
  };

  const orderDateTime = String(item.onDateTime || "").trim();
  const parsedOrderDateTime = orderDateTime ? new Date(orderDateTime) : null;
  const [date, time] =
    parsedOrderDateTime && !Number.isNaN(parsedOrderDateTime.getTime())
      ? [format(parsedOrderDateTime, "MM/dd/yyyy"), format(parsedOrderDateTime, "HH:mm:ss")]
      : [formatDate(item.onDate), formatTime(item.onTime)];

  const scheduledDate = formatDate(item.onDate);
  const scheduledDateTime = formatRequestedDateTime(item.onDate, item.onTime);
  const isScheduledSameDay = scheduledDate === date;
  const isTakeOut = String(item.orderType || "").toLowerCase().includes("take");
  const scheduleLabel = isTakeOut ? "Pickup" : "Delivery";
  const typeChipClass = isTakeOut
    ? "border-amber-200 bg-amber-100 text-amber-800"
    : "border-slate-200 bg-slate-50 text-slate-700";
  const scheduleChipClass = isScheduledSameDay
    ? isTakeOut
      ? "border-sky-200/80 bg-sky-50/80 text-sky-800 shadow-[0_8px_20px_rgba(14,165,233,0.1)]"
      : "border-blue-200/80 bg-blue-50/80 text-blue-800 shadow-[0_8px_20px_rgba(37,99,235,0.1)]"
    : "border-red-300/80 bg-red-50/75 text-red-700 shadow-[0_0_0_1px_rgba(248,113,113,0.2),0_10px_24px_rgba(239,68,68,0.16)] animate-[scheduleAlert_1.9s_ease-in-out_infinite]";

  const getDescriptionParts = (subItem: any) => {
    const raw = String(subItem?.description || "").trim();
    if (!raw) return [];

    const normalized = raw.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
    const productLabel = String(subItem?.productCode || "").trim().toUpperCase();

    return normalized
      .split(/\n|\||\*/)
      .map((line) => line.trim())
      .filter(Boolean)
      .filter(
        (line, index) =>
          !(index === 0 && productLabel && line.toUpperCase() === productLabel),
      )
      .filter((line) => !/^spice\s*:\s*(\[object object\])?\s*$/i.test(line));
  };

  const parseMaybeJson = (value: any): any => {
    if (typeof value !== "string") return value;

    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith("[") && !trimmed.startsWith("{"))) return value;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const toModifierArray = (value: any): any[] => {
    const parsed = parseMaybeJson(value);

    if (Array.isArray(parsed)) return parsed.flatMap(toModifierArray);
    if (parsed && typeof parsed === "object") {
      if (Array.isArray(parsed.modifiers)) return parsed.modifiers.flatMap(toModifierArray);
      if (Array.isArray(parsed.selectedItemsLevel)) {
        return parsed.selectedItemsLevel.flatMap(toModifierArray);
      }
      if (Array.isArray(parsed.selectedItems)) return parsed.selectedItems.flatMap(toModifierArray);
      if (Array.isArray(parsed.orderModifiers)) return parsed.orderModifiers.flatMap(toModifierArray);
      if (Array.isArray(parsed.orderDetailModifiers)) {
        return parsed.orderDetailModifiers.flatMap(toModifierArray);
      }

      const hasModifierName = !!String(
        parsed.description ||
          parsed.modifierName ||
          parsed.pizzaModifierName ||
          parsed.name ||
          "",
      ).trim();

      if (hasModifierName) return [parsed];

      return Object.values(parsed).flatMap(toModifierArray);
    }

    return [];
  };

  const getDirectModifiers = (subItem: any) =>
    [
      subItem?.modifiers,
      subItem?.modifier,
      subItem?.modifierList,
      subItem?.modifiersList,
      subItem?.modifierDetails,
      subItem?.orderModifiers,
      subItem?.orderModifier,
      subItem?.orderDetailModifiers,
      subItem?.orderDetailModifier,
      subItem?.selectedItemsLevel,
      subItem?.selectedItems,
      subItem?.itemsLevel,
      subItem?.selectedItemLevels,
      subItem?.cartModifiers,
      subItem?.savedCartModifiers,
      subItem?.toppings,
      subItem?.pizzaToppings,
      subItem?.jsonData,
      subItem?.data,
      subItem?.data?.modifiers,
      subItem?.data?.selectedItemsLevel,
    ]
      .flatMap(toModifierArray)
      .filter((modifier: any) => {
        const name = String(
          modifier?.description ||
            modifier?.modifierName ||
            modifier?.pizzaModifierName ||
            modifier?.name ||
            "",
        ).trim();

        return !!name;
      });

  const getDescriptionModifierFallback = (subItem: any) =>
    getDescriptionParts(subItem)
      .filter((line) => /^(modifiers?|options?|toppings?)\s*:/i.test(line))
      .flatMap((line) =>
        line
          .replace(/^(modifiers?|options?|toppings?)\s*:/i, "")
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean),
      )
      .map((line) => {
        const priceMatch = line.match(/\$?\s*(\d+(?:\.\d{1,2})?)\s*$/);
        const price = priceMatch ? Number(priceMatch[1]) : 0;
        const description = priceMatch
          ? line.slice(0, priceMatch.index).replace(/[[\]()]/g, "").trim()
          : line;

        return { description, unitPrice: price };
      })
      .filter((modifier) => modifier.description);

  const getModifierGroups = (subItem: any) => {
    const buildOptionGroups = (modifiers: any[], optionDetail: any) => {
      const grouped = modifiers.reduce((groups: Record<string, any[]>, modifier: any) => {
        const label = getModifierOptionGroupLabel(modifier, "Modifiers");
        groups[label] = [...(groups[label] || []), modifier];
        return groups;
      }, {});

      return Object.entries(grouped).map(([title, groupedModifiers]) => ({
        title,
        detail: optionDetail,
        modifiers: groupedModifiers,
        showPizzaControls: false,
      }));
    };

    const getDealPizzas = (item: any) => {
      if (Array.isArray(item?.selectedDealPizzas)) return item.selectedDealPizzas;
      if (Array.isArray(item?.data?.selectedDealPizzas)) return item.data.selectedDealPizzas;
      return [];
    };

    const buildDealPizzaGroups = (item: any) =>
      getDealPizzas(item)
        .map((dealPizza: any, index: number) => ({
          title:
            dealPizza?.pizzaName ||
            dealPizza?.fullDescription ||
            dealPizza?.description ||
            `Pizza ${index + 1}`,
          detail: dealPizza,
          modifiers: Array.isArray(dealPizza?.modifiers) ? dealPizza.modifiers : [],
          showPizzaControls: true,
        }))
        .filter((group: any) => group.modifiers.length);

    const directModifiers = getDirectModifiers(subItem);
    const dealPizzaGroups = buildDealPizzaGroups(subItem);

    if (dealPizzaGroups.length) {
      return [
        ...buildOptionGroups(directModifiers, subItem),
        ...dealPizzaGroups,
      ].filter((group) => group.modifiers.length);
    }

    if (directModifiers.length) {
      if (isPizzaOrderDetail(subItem)) {
        const removedModifiers = getRemovedModifiersFromDescription(subItem);
        const toppingModifiers = directModifiers.filter(
          (modifier: any) => getModifierRegion(modifier) > 0,
        );
        const optionModifiers = directModifiers.filter(
          (modifier: any) => getModifierRegion(modifier) === 0,
        );

        return [
          {
            title: "Toppings",
            detail: subItem,
            modifiers: [...toppingModifiers, ...removedModifiers],
            showPizzaControls: true,
          },
          ...buildOptionGroups(optionModifiers, subItem),
        ].filter((group) => group.modifiers.length);
      }

      return buildOptionGroups(directModifiers, subItem);
    }

    const fallbackModifiers = getDescriptionModifierFallback(subItem);

    if (fallbackModifiers.length) {
      return [
        {
          title: "Modifiers",
          detail: subItem,
          modifiers: fallbackModifiers,
          showPizzaControls: false,
        },
      ];
    }

    const removedModifiers = getRemovedModifiersFromDescription(subItem);
    if (removedModifiers.length) {
      return [
        {
          title: "Toppings",
          detail: subItem,
          modifiers: removedModifiers,
          showPizzaControls: true,
        },
      ];
    }

    return [];
  };

  const renderModifierChips = (subItem: any) => {
    const groups = getModifierGroups(subItem);
    if (!groups.length) return null;
    const getGroupNoteText = (group: any) =>
      String(group?.detail?.note || group?.detail?.notes || "").trim();
    const getGroupPreferenceText = (group: any) => {
      const detail = group?.detail || {};
      const hasDealPizzaDetails =
        Array.isArray(detail?.selectedDealPizzas) ||
        Array.isArray(detail?.data?.selectedDealPizzas);

      if (String(group?.title || "").toLowerCase() === "modifiers" && hasDealPizzaDetails) {
        return "";
      }

      const crust = String(
        detail?.pizzaCrust?.crustName || detail?.pizzaCrust?.modifierName || "",
      ).trim();
      const size = String(
        detail?.pizzaSize?.size || detail?.pizzaSize?.sizeName || "",
      ).trim();
      const spice = String(
        detail?.spiceLevel?.spicename || detail?.spiceLevel?.modifierName || "",
      ).trim();

      return [
        crust ? `CRUST:${crust}` : "",
        size ? `Size: ${size}` : "",
        spice ? `Spice: ${spice}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
    };
    const getModifierDisplayPrice = (modifier: any) => {
      if (modifier?.subTotal !== undefined && modifier?.subTotal !== null) {
        return Number(modifier.subTotal || 0);
      }

      return Number(
        modifier?.totalValue ||
          modifier?.unitPrice ||
          modifier?.price ||
          modifier?.amount ||
          modifier?.modifierPrice ||
          modifier?.pizzaModifierPrice ||
          0,
      );
    };

    return (
      <div className="mt-2 flex min-w-0 flex-col gap-1.5">
        {groups.map((group: any, groupIndex: number) => {
          const isPizza = !!group.showPizzaControls;

          return (
            <div
              key={`${group.title}-${groupIndex}`}
              className="flex min-w-0 flex-wrap items-start gap-2 border-t border-slate-100 pt-1.5 first:border-t-0 first:pt-0"
            >
              <div className="mt-1 min-w-[92px] shrink-0">
                <p className="text-[11px] font-bold leading-4 text-slate-600">
                  {group.title}
                </p>
                {!!getGroupPreferenceText(group) && (
                  <p className="mt-0.5 text-[10px] font-semibold leading-4 text-slate-500">
                    {getGroupPreferenceText(group)}
                  </p>
                )}
                {!!getGroupNoteText(group) && (
                  <p className="mt-0.5 text-[10px] font-semibold leading-4 text-slate-500">
                    Note: {getGroupNoteText(group)}
                  </p>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {group.modifiers.map((modifier: any, modifierIndex: number) => {
                const isRemovedModifier = !!modifier?.isRemovedModifier;
                const parts = getModifierDisplayParts(
                  group.showPizzaControls
                    ? group.detail
                    : {
                        isPizza: false,
                        isPizzaItem: false,
                        isHalfPizzaItem: false,
                        pizzaSize: null,
                        pizzaCrust: null,
                        description: "",
                        note: "",
                      },
                  modifier,
                );
                if (!parts.name) return null;
                const price = getModifierDisplayPrice(modifier);

                return (
                  <span
                    key={`${parts.name}-${modifierIndex}`}
                    className={`inline-flex max-w-full flex-wrap items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold leading-4 ${
                      isRemovedModifier
                        ? "border-red-200 bg-red-50 text-red-800"
                        : isPizza
                        ? "border-blue-100 bg-blue-50 text-slate-900"
                        : "border-slate-200 bg-slate-50 text-slate-800"
                    }`}
                  >
                    {isRemovedModifier && (
                      <span className="rounded bg-red-100 px-1 py-0.5 text-[9px] font-bold uppercase text-red-700 ring-1 ring-red-200">
                        No
                      </span>
                    )}
                    {!!parts.sideLabel && (
                      <span className="rounded bg-white px-1 py-0.5 text-[9px] font-bold uppercase text-blue-700 ring-1 ring-blue-100">
                        {parts.sideLabel}
                      </span>
                    )}
                    {!!parts.multiplierLabel && (
                      <span className="rounded bg-slate-950 px-1 py-0.5 text-[9px] font-bold uppercase text-white">
                        {parts.multiplierLabel}
                      </span>
                    )}
                    <span className="break-words">{parts.name}</span>
                    {price > 0 && (
                      <span className="rounded bg-white/80 px-1 py-0.5 text-[9px] font-bold text-slate-600 ring-1 ring-slate-200">
                        ${price.toFixed(2)}
                      </span>
                    )}
                  </span>
                );
              })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getItemNote = (subItem: any) => {
    if (String(subItem?.note || "").trim()) return subItem.note;
    if (String(subItem?.notes || "").trim()) return subItem.notes;

    const dealPizzaNotes = Array.isArray(subItem?.data?.selectedDealPizzas)
      ? subItem.data.selectedDealPizzas
          .map((dealPizza: any, index: number) => {
            const note = String(dealPizza?.note || "").trim();
            if (!note) return "";
            const pizzaName =
              dealPizza?.pizzaName || dealPizza?.fullDescription || `Pizza ${index + 1}`;
            return `${pizzaName}: ${note}`;
          })
          .filter(Boolean)
      : [];

    return dealPizzaNotes.join(" | ");
  };

  const getItemDescription = (subItem: any) => {
    const hasDealPizzaDetails =
      Array.isArray(subItem?.selectedDealPizzas) ||
      Array.isArray(subItem?.data?.selectedDealPizzas);

    if (hasDealPizzaDetails) return "";

    return getDescriptionParts(subItem)
      .filter((line) => /^(crust|size|spice)\s*:/i.test(line))
      .join(" | ");
  };

  const getTaxLabel = (index: number, fallback: string) =>
    item?.taxData?.[index]?.name || item?.taxData?.[index]?.taxName || fallback;
  const taxRows = [
    { label: getTaxLabel(0, TAX_TYPE.tax1), amount: Number(item.totalTax1 || 0) },
    { label: getTaxLabel(1, TAX_TYPE.tax2), amount: Number(item.totalTax2 || 0) },
    { label: getTaxLabel(2, TAX_TYPE.tax3), amount: Number(item.totalTax3 || 0) },
  ].filter((row) => row.amount > 0);
  const noteText = String(item.notes || "").trim();
  const paymentType = String(item.paymentType || "").trim() || "Pay at Counter";
  const customerName = [
    (item as any)?.customer?.firstName ?? (item as any)?.firstName,
    (item as any)?.customer?.lastName ?? (item as any)?.lastName,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const firstText = (...values: any[]) =>
    values.map((value) => String(value || "").trim()).find(Boolean) || "";
  const customerAddressType = firstText(
    (item as any)?.AddressType,
    (item as any)?.addressType,
    (item as any)?.customer?.addressType,
  );
  const customerAddress = [
    firstText(
      (item as any)?.address,
      (item as any)?.address1,
      (item as any)?.customer?.address,
      (item as any)?.customer?.address1,
    ),
  ].filter(Boolean).join(", ");
  const PlaceIcon =
    customerAddressType.toLowerCase() === "home"
      ? HomeOutlinedIcon
      : customerAddressType.toLowerCase() === "office"
      ? WorkOutlineOutlinedIcon
      : PlaceOutlinedIcon;
  const deliveryCharge = Number(item.deliveryCharge || 0);
  const lineDiscountAmount = Array.isArray(item.orderDetail)
    ? item.orderDetail.reduce(
        (sum: number, detail: any) =>
          sum + Number(detail?.discount || detail?.discAmt || detail?.promoDiscount || 0),
        0,
      )
    : 0;
  const discountAmount =
    [
      (item as any).promoDiscount,
      (item as any).promoDiscAmt,
      (item as any).discAmt,
      item.discount,
      (item as any).discountAmount,
      lineDiscountAmount,
    ]
      .map((value) => Number(value || 0))
      .find((value) => value > 0) || 0;
  return (
    <div
      className={`w-full cursor-pointer select-none rounded-lg border px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.06)] transition-all hover:border-primary/25 hover:shadow-[0_10px_24px_rgba(15,23,42,0.1)] ${
        item.isNew
          ? "dcs-new-order-blink border-rose-300 bg-white"
          : "border-slate-200/80 bg-white"
      }`}
      onClick={() => setshowProducts(!showProducts)}
      onDoubleClick={item.onDoubleClick}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200/70 px-1 pb-1.5">
          <p className="mr-1 text-[13px] font-semibold text-slate-900 sm:text-[14px]">
            Order ID : <span className="font-bold">{item.orderID}</span>
            {!!customerName && (
              <span className="ml-2 font-bold">{customerName}</span>
            )}
          </p>
          <span className="ml-auto shrink-0 rounded-full border border-primary/15 bg-primary/10 p-1 shadow-sm">
            {showProducts ? (
              <KeyboardArrowUpIcon className="!text-[18px]" />
            ) : (
              <KeyboardArrowDownIcon className="!text-[18px]" />
            )}
          </span>
      </div>

      <div className="flex min-w-0 items-start gap-2 pt-2">
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-1 items-start gap-2 px-1 pb-1.5 xl:grid-cols-[minmax(0,1fr)_245px]">
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className={`rounded border px-2 py-0.5 font-semibold ${typeChipClass}`}>
                  Type: <span className="text-inherit">{item.orderType || "--"}</span>
                </span>
                <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                  Date: <span className="text-slate-950">{date || "--"}</span>
                </span>
                <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                  Time: <span className="text-slate-950">{time || "--"}</span>
                </span>
                <span
                  className={`rounded border px-2 py-0.5 font-semibold backdrop-blur-sm ${scheduleChipClass}`}
                >
                  {scheduleLabel}:{" "}
                  <span className="text-inherit">
                    {scheduledDateTime}
                  </span>
                </span>
                <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                  Payment: <span className="text-slate-950">{paymentType}</span>
                </span>
              </div>
              <div className="rounded border border-rose-100 bg-rose-50/55 px-2 py-1">
                <p className="text-[9px] uppercase text-slate-500 font-semibold">
                  Note
                </p>
                <p className="text-[12px] sm:text-[13px] leading-5 text-slate-800 break-words">
                  {noteText || "--"}
                </p>
              </div>
              {item.orderType === "Delivery" && (
                <div className="mt-1.5 rounded border border-blue-100 bg-blue-50/70 px-2 py-1">
                  <p className="text-[9px] uppercase text-slate-500 font-semibold">
                    Delivery Address
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {!!customerAddressType && (
                      <span className="inline-flex w-fit shrink-0 items-center gap-1 rounded border border-blue-100 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 shadow-sm">
                        <PlaceIcon className="!text-[13px] text-primary" />
                        {customerAddressType}
                      </span>
                    )}
                    <p className="min-w-0 text-[12px] sm:text-[13px] leading-5 text-slate-800 break-words">
                      {customerAddress || "--"}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-0 rounded border border-slate-200 bg-white px-2 py-1 shadow-sm">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                <span className="text-[9px] uppercase text-slate-500 font-semibold">
                  Sub Total
                </span>
                <span className="text-[12px] font-semibold leading-5 text-slate-900">
                  ${Number(item.subTotal || 0).toFixed(2)}
                </span>
              </div>
              {!!discountAmount && (
                <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                  <span className="text-[9px] uppercase text-[#f10505] font-bold">
                    Special Discount
                  </span>
                  <span className="text-[12px] font-bold leading-5 text-[#f10505]">
                    -${discountAmount.toFixed(2)}
                  </span>
                </div>
              )}
              {taxRows.map((taxRow) => (
                <div
                  key={taxRow.label}
                  className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5"
                >
                  <span className="text-[9px] uppercase text-slate-500 font-semibold">
                    {taxRow.label}
                  </span>
                  <span className="text-[12px] font-semibold leading-5 text-slate-900">
                    ${taxRow.amount.toFixed(2)}
                  </span>
                </div>
              ))}
              {!!deliveryCharge && (
                <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                  <span className="text-[9px] uppercase text-slate-500 font-semibold">
                    Delivery Charge
                  </span>
                  <span className="text-[12px] font-semibold leading-5 text-slate-900">
                    ${deliveryCharge.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                <span className="text-[9px] uppercase text-slate-500 font-semibold">
                  Staff Tip
                </span>
                <span className="text-[12px] font-semibold leading-5 text-slate-900">
                  ${Number(item.tip || 0).toFixed(2)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-3 rounded bg-slate-950 px-2 py-1 text-white shadow-sm">
                <span className="text-[9px] uppercase text-white/70 font-semibold">
                  Order Total
                </span>
                <span className="text-[15px] font-extrabold leading-6 text-white">
                  ${item.orderTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <AnimatePresence>
            <motion.div
              className="overflow-hidden"
              initial={{ height: 0 }}
              animate={{ height: showProducts ? "auto" : 0 }}
            >
              <div
                className="mt-1.5 min-w-0 overflow-x-auto rounded border border-slate-200 bg-white"
              >
                <h2
                  // === ADDED BY SAMPATH – 2026-01-06 ===
                  // Cleaner section title
                  className="px-2.5 py-1.5 font-semibold text-slate-900 text-[13px]"
                  // === END SAMPATH ===
                >
                  Product Details
                </h2>

                <div
                  className="flex min-w-[720px] border-y border-slate-200 bg-slate-50 px-2.5 py-1 text-[12px] font-semibold"
                >
                  <p className="flex-[5] sm:flex-[3.2]">Product</p>
                  <p className="w-20 sm:w-24 shrink-0">Qty</p>
                  <p className="w-24 sm:w-32 shrink-0">Note</p>
                  {!!item.totalTax1 && (
                    <p className="w-16 sm:w-20 shrink-0 max-2md:hidden">
                      {item?.taxData?.[0]?.name || TAX_TYPE.tax1}
                    </p>
                  )}
                  {!!item.totalTax2 && (
                    <p className="w-16 sm:w-20 shrink-0 max-2md:hidden">
                      {item?.taxData?.[1]?.name || TAX_TYPE.tax2}
                    </p>
                  )}
                  {!!item.totalTax3 && (
                    <p className="w-16 sm:w-20 shrink-0 max-2md:hidden">
                      {item?.taxData?.[2]?.name || TAX_TYPE.tax3}
                    </p>
                  )}
                  <p className="flex-1 text-right">Total</p>
                </div>

                <div
                  className="mx-2.5 flex min-w-[696px] flex-col divide-y divide-slate-100"
                >
                  {item.orderDetail.map((subItem, idx) => (
                    <div
                      className="py-1.5 text-[12px]"
                      key={`${subItem?.productCode || "item"}-${idx}`}
                    >
                      <div className="flex items-start">
                        <div className="flex-[5] sm:flex-[3.2] min-w-0 pr-3">
                          <p className="break-words font-bold text-slate-900">
                            {subItem.productCode || subItem.description || "--"}
                          </p>
                          {!!getItemDescription(subItem) && (
                            <p className="text-[11px] text-slate-500 mt-0.5 break-words leading-4">
                              {getItemDescription(subItem)}
                            </p>
                          )}
                        </div>

                        <p className="w-20 sm:w-24 shrink-0 text-slate-800">{subItem.quantity}</p>

                        <Tooltip title={getItemNote(subItem)} arrow placement="top">
                          <p
                            // === ADDED BY SAMPATH – 2026-01-06 ===
                            // Prevent long note from breaking row height
                            className="w-24 sm:w-32 shrink-0 truncate text-slate-700 pr-2"
                            // === END SAMPATH ===
                          >
                            {getItemNote(subItem) || "--"}
                          </p>
                        </Tooltip>

                        {!!item.totalTax1 && (
                          <p className="w-16 sm:w-20 shrink-0 max-2md:hidden text-slate-800">
                            ${subItem.tax1.toFixed(2)}
                          </p>
                        )}
                        {!!item.totalTax2 && (
                          <p className="w-16 sm:w-20 shrink-0 max-2md:hidden text-slate-800">
                            ${subItem.tax2.toFixed(2)}
                          </p>
                        )}
                        {!!item.totalTax3 && (
                          <p className="w-16 sm:w-20 shrink-0 max-2md:hidden text-slate-800">
                            ${subItem.tax3.toFixed(2)}
                          </p>
                        )}

                        <p
                          // === ADDED BY SAMPATH – 2026-01-06 ===
                          // Right-align totals like a report table
                          className="flex-1 text-right font-medium text-slate-900"
                          // === END SAMPATH ===
                        >
                          ${subItem.totalValue}
                        </p>
                      </div>
                      {renderModifierChips(subItem)}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

export default OrderCard;
